import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { getAuthHeader } from "../../utils/authToken";
import { fmtNyDateTime } from "./prepDates";
import { canUseDevTools } from "../../utils/devToolsAccess";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;
const HAS_ACCESS = canUseDevTools();

// Helper page: release / preview prep homework rounds without waiting on the cron.
// Mirrors `processPrepRound(roundId, dryRun)` via the dev-run endpoint. Available in
// local dev and, on production, to the allowlisted admins (see utils/devToolsAccess.js /
// the server's DEV_TOOLS_ADMIN_EMAILS, which is the real gate).
const PrepDevTools = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    const auth = { headers: { Authorization: getAuthHeader() } };

    const [programs, setPrograms] = useState([]);
    const [programUuid, setProgramUuid] = useState("");
    const [rounds, setRounds] = useState([]);
    const [loadingRounds, setLoadingRounds] = useState(false);
    const [busyRound, setBusyRound] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!HAS_ACCESS) return;
        Axios.get(BASE, auth).then((res) => setPrograms(res.data || [])).catch(() => setPrograms([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadRounds = useCallback(async (uuid) => {
        if (!uuid) {
            setRounds([]);
            return;
        }
        setLoadingRounds(true);
        try {
            const res = await Axios.get(`${BASE}/${uuid}/homework-rounds`, { headers: { Authorization: getAuthHeader() } });
            setRounds(res.data || []);
        } catch (e) {
            setRounds([]);
        } finally {
            setLoadingRounds(false);
        }
    }, []);

    const onSelectProgram = (uuid) => {
        setProgramUuid(uuid);
        setResult(null);
        setError("");
        loadRounds(uuid);
    };

    const runRound = async (round, dryRun) => {
        if (!dryRun && !window.confirm("Release for real? This performs ETT assignments and sends assignment emails.")) {
            return;
        }
        setBusyRound(`${round.id}:${dryRun}`);
        setResult(null);
        setError("");
        try {
            const res = await Axios.post(`${BASE}/${programUuid}/homework-rounds/${round.id}/dev-run`, { dryRun }, auth);
            setResult({ round: round.id, dryRun, data: res.data });
            if (!dryRun) loadRounds(programUuid); // refresh is_released
        } catch (e) {
            setError(e.response?.data?.message || e.response?.data?.error || "Run failed");
        } finally {
            setBusyRound(null);
        }
    };

    if (!HAS_ACCESS) {
        return (
            <Box m="20px">
                <Alert severity="info">You don't have access to Prep Dev Tools.</Alert>
            </Box>
        );
    }

    return (
        <Box m="20px">
            <Box display="flex" alignItems="center" gap={1} mb="16px">
                <IconButton onClick={() => navigate("/admin/prepPrograms")}><ArrowBackIcon /></IconButton>
                <Typography variant="h4" fontWeight="bold">Prep Dev Tools</Typography>
                <Chip label="DEV" color="warning" size="small" sx={{ ml: 1 }} />
            </Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
                Manually release or preview homework rounds (each round normally releases on its own timer at its exact release time; the 9:10 AM ET scan is the backstop).
                <strong> Release</strong> makes real ETT assignments and sends emails — use <strong>Dry-run</strong> to
                preview safely first. Restricted to authorized admins.
            </Alert>

            <FormControl size="small" sx={{ minWidth: 300, mb: 2 }}>
                <InputLabel>Program</InputLabel>
                <Select label="Program" value={programUuid} onChange={(e) => onSelectProgram(e.target.value)}>
                    {programs.map((p) => (
                        <MenuItem key={p.uuid} value={p.uuid}>{p.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {loadingRounds ? (
                <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
            ) : programUuid && rounds.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No homework rounds for this program.</Typography>
            ) : rounds.length > 0 ? (
                <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600], mb: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Round</TableCell>
                                <TableCell>Release</TableCell>
                                <TableCell>Due</TableCell>
                                <TableCell>Mode</TableCell>
                                <TableCell>Released</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rounds.map((r) => (
                                <TableRow key={r.id}>
                                    <TableCell>#{r.id}{r.label ? ` · ${r.label}` : ""}</TableCell>
                                    <TableCell>{fmtNyDateTime(r.release_date)}</TableCell>
                                    <TableCell>{fmtNyDateTime(r.due_date)}</TableCell>
                                    <TableCell>{r.assign_mode || "ALL"}</TableCell>
                                    <TableCell>
                                        {r.is_released
                                            ? <Chip size="small" color="success" label="released" />
                                            : <Chip size="small" label="no" />}
                                    </TableCell>
                                    <TableCell>
                                        <Button size="small" disabled={!!busyRound} onClick={() => runRound(r, true)} sx={{ mr: 1 }}>
                                            {busyRound === `${r.id}:true` ? <CircularProgress size={14} /> : "Dry-run"}
                                        </Button>
                                        <Button size="small" variant="contained" color="warning" disabled={!!busyRound} onClick={() => runRound(r, false)}>
                                            {busyRound === `${r.id}:false` ? <CircularProgress size={14} color="inherit" /> : "Release"}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : null}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {result && (
                <Paper sx={{ p: 2, backgroundColor: colors.primary[700] }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Round #{result.round} — {result.dryRun ? "Dry-run (no writes / ETT / emails)" : "Released"}
                    </Typography>
                    <Box component="pre" sx={{ m: 0, fontSize: 12, overflow: "auto", maxHeight: 420, whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(result.data, null, 2)}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default PrepDevTools;
